import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import { authApi } from '../../api/authApi';
import { DEFAULT_CLIENT_CONFIG } from '../../api/configApi';
import IClientConfig from '../../models/IClientConfig';
import { useAppState } from '../../reduxish/appState';

interface Props {
    open: boolean;
    onClose: () => void;
}

function buildPasswordHint(c: IClientConfig): string {
    const reqs: string[] = [];
    if (c.passwordRequireUpper) reqs.push('uppercase');
    if (c.passwordRequireLower) reqs.push('lowercase');
    if (c.passwordRequireDigit) reqs.push('number');
    if (c.passwordRequireSpecial) reqs.push('special character');
    const reqText = reqs.length > 0 ? ` with ${reqs.join(', ')}` : '';
    return `At least ${c.passwordMinLength} characters${reqText}`;
}

export default function ChangePasswordDialog({ open, onClose }: Props) {
    const { state } = useAppState();
    const clientConfig = state.clientConfig ?? DEFAULT_CLIENT_CONFIG;
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const reset = () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setError('');
    };

    const handleClose = () => {
        if (submitting) return;
        reset();
        onClose();
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!currentPassword || !newPassword) {
            setError('Please fill in all fields.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('New passwords do not match.');
            return;
        }
        if (newPassword.length < clientConfig.passwordMinLength) {
            setError(`Password must be at least ${clientConfig.passwordMinLength} characters long.`);
            return;
        }

        setSubmitting(true);
        try {
            await authApi.changePassword(currentPassword, newPassword);
            reset();
            onClose();
        } catch (err: any) {
            setError(err?.message || 'Could not change your password.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <form onSubmit={handleSubmit}>
                <DialogTitle>Change password</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        {buildPasswordHint(clientConfig)}.
                    </DialogContentText>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <Stack spacing={2}>
                        <TextField
                            type="password"
                            label="Current password"
                            fullWidth
                            value={currentPassword}
                            onChange={e => setCurrentPassword(e.target.value)}
                            disabled={submitting}
                            autoComplete="current-password"
                        />
                        <TextField
                            type="password"
                            label="New password"
                            fullWidth
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            disabled={submitting}
                            autoComplete="new-password"
                        />
                        <TextField
                            type="password"
                            label="Confirm new password"
                            fullWidth
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            disabled={submitting}
                            autoComplete="new-password"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" disabled={submitting}>
                        {submitting ? 'Saving…' : 'Change password'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
